<template>
  <div id="app" class="min-h-screen flex flex-col" lang="en">
    <!-- Skip to main content for accessibility -->
    <a 
      v-if="!isBare"
      href="#main-content" 
      class="skip-to-main"
      @click="focusMainContent"
    >
      Skip to main content
    </a>

    <!-- ServiceVision.org network family bar -->
    <nav v-if="!isBare" class="sv-familybar" aria-label="ServiceVision.org network">
      <a class="svf-home" href="https://www.servicevision.org">◆ ServiceVision.org</a>
      <span class="svf-sep">network:</span>
      <a data-sv="secondchance" href="https://secondchance.dev">Second Chance · AI &amp; Upskilling</a>
      <a data-sv="powerhouse" href="https://powerhousetraining.org">PowerHouse · Applied Center</a>
      <a data-sv="lastmile" href="https://lastmileguru.dev">LastMileGuru.dev · DevOps</a>
    </nav>

    <!-- Header/Navigation -->
    <NavBar v-if="!isBare" />

    <!-- Main content -->
    <main 
      id="main-content" 
      class="flex-grow"
      tabindex="-1"
      role="main"
      :aria-label="currentPageTitle"
    >
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>

    <!-- Footer -->
    <AppFooter v-if="!isBare" />
    
    <!-- Toast notifications -->
    <ToastContainer />

    <!-- Keyboard shortcuts modal -->
    <div 
      v-if="showKeyboardShortcuts"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="keyboard-shortcuts-title"
      @keydown.esc="showKeyboardShortcuts = false"
      @click.self="showKeyboardShortcuts = false"
    >
      <div class="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
        <h2 id="keyboard-shortcuts-title" class="text-xl font-bold text-gray-900 dark:text-white mb-4">
          Keyboard Shortcuts
        </h2>
        <div class="space-y-2 text-sm">
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Skip to main content:</span>
            <kbd class="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">Tab</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Toggle dark mode:</span>
            <kbd class="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">Alt + D</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Show shortcuts:</span>
            <kbd class="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">?</kbd>
          </div>
          <div class="flex justify-between">
            <span class="text-gray-600 dark:text-gray-400">Close dialog:</span>
            <kbd class="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">Esc</kbd>
          </div>
        </div>
        <button
          @click="showKeyboardShortcuts = false"
          class="mt-4 w-full btn-primary"
          aria-label="Close keyboard shortcuts dialog"
        >
          Close
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import NavBar from '@/components/NavBar.vue'
import AppFooter from '@/components/AppFooter.vue'
import ToastContainer from '@/components/ToastContainer.vue'
import { organizationSchema, websiteSchema, injectStructuredData } from '@/utils/structuredData.js'

const router = useRouter()
const route = useRoute()
const showKeyboardShortcuts = ref(false)

// Current page title for accessibility
const currentPageTitle = computed(() => {
  return route.meta?.title || 'SecondChance.dev'
})

// Routes flagged `bare` render without site chrome — no family bar, nav, or
// footer. Used by /reentry, where every extra link is a way to lose someone
// who is mid-application on a library computer with a session timer.
const isBare = computed(() => route.meta?.bare === true)

// Focus main content when skip link is clicked
const focusMainContent = () => {
  const mainContent = document.getElementById('main-content')
  if (mainContent) {
    mainContent.focus()
  }
}

// Global keyboard shortcuts
const handleKeydown = (event) => {
  // Show keyboard shortcuts on '?' key
  if (event.key === '?' && !event.ctrlKey && !event.altKey && !event.metaKey) {
    event.preventDefault()
    showKeyboardShortcuts.value = true
    return
  }

  // Toggle dark mode with Alt+D
  if (event.altKey && event.key.toLowerCase() === 'd') {
    event.preventDefault()
    toggleDarkMode()
    return
  }

  // Close modals with Escape
  if (event.key === 'Escape') {
    if (showKeyboardShortcuts.value) {
      showKeyboardShortcuts.value = false
    }
  }
}

// Dark mode toggle function (shared with NavBar)
const toggleDarkMode = () => {
  const isDark = document.documentElement.classList.contains('dark')
  if (isDark) {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('darkMode', 'false')
  } else {
    document.documentElement.classList.add('dark')
    localStorage.setItem('darkMode', 'true')
  }
}

// Handle initial setup
onMounted(async () => {
  // Check for saved theme preference or default to light
  const savedTheme = localStorage.getItem('darkMode')
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  
  if (savedTheme === 'true' || (!savedTheme && prefersDark)) {
    document.documentElement.classList.add('dark')
  }
  
  // Add global keyboard event listeners
  document.addEventListener('keydown', handleKeydown)
  
  // Inject structured data for SEO
  injectStructuredData(organizationSchema)
  injectStructuredData(websiteSchema)
  
  // Initialize auth store
  const { useAuthStore } = await import('@/stores/auth')
  const authStore = useAuthStore()
  await authStore.initialize()
  
  // Log navigation for development
  if (import.meta.env.DEV) {
    router.afterEach((to, from) => {
      console.log(`Navigated from ${from.path} to ${to.path}`)
    })
  }

  // Announce page changes to screen readers
  router.afterEach((to) => {
    // Update page title for screen readers
    document.title = to.meta?.title || 'SecondChance.dev'
    
    // Announce route change to screen readers
    const announcement = `Navigated to ${to.meta?.title || to.path}`
    const ariaLiveRegion = document.createElement('div')
    ariaLiveRegion.setAttribute('aria-live', 'polite')
    ariaLiveRegion.setAttribute('aria-atomic', 'true')
    ariaLiveRegion.className = 'sr-only'
    ariaLiveRegion.textContent = announcement
    document.body.appendChild(ariaLiveRegion)
    
    // Remove announcement after screen reader has time to read it
    setTimeout(() => {
      document.body.removeChild(ariaLiveRegion)
    }, 1000)
  })
})

// Cleanup event listeners
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})
</script>

<style>
/* Page transition animations */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Smooth scroll behavior */
html {
  scroll-behavior: smooth;
}

/* Focus visible only on keyboard navigation */
:focus:not(:focus-visible) {
  outline: none;
}

/* ServiceVision.org Family Bar — shared snippet v1.0.0 (inlined; namespaced, self-contained) */
.sv-familybar {
  --svf-surface:#141a21; --svf-line:#26303b; --svf-ink:#eef1f5; --svf-faint:#5f6c7a;
  --svf-gold:#e0b44e; --svf-sc:#43c08c; --svf-ph:#ff7a32; --svf-lm:#5aa6e6;
  --svf-mono:ui-monospace,"SF Mono","Cascadia Code",Menlo,Consolas,monospace;
}
@media (prefers-color-scheme: light) {
  .sv-familybar { --svf-surface:#fbfaf7; --svf-line:#ded8cc; --svf-ink:#191c21; --svf-faint:#8a93a0; --svf-gold:#b07d18; --svf-sc:#1e8e63; --svf-ph:#d9550f; --svf-lm:#2c74b0; }
}
.sv-familybar { display:flex; flex-wrap:wrap; align-items:center; gap:6px 8px; background:var(--svf-surface); border-bottom:1px solid var(--svf-line); padding:9px clamp(12px,4vw,28px); font-family:var(--svf-mono); font-size:12.5px; }
.sv-familybar .svf-home { color:var(--svf-gold); font-weight:600; text-decoration:none; }
.sv-familybar .svf-sep { color:var(--svf-faint); font-size:10.5px; letter-spacing:.1em; text-transform:uppercase; margin:0 2px; }
.sv-familybar a[data-sv] { color:var(--svf-ink); text-decoration:none; padding:3px 9px; border-radius:4px; border:1px solid transparent; }
.sv-familybar a[data-sv="secondchance"] { color:var(--svf-sc); border-color:color-mix(in srgb,var(--svf-sc) 30%,transparent); }
.sv-familybar a[data-sv="powerhouse"] { color:var(--svf-ph); border-color:color-mix(in srgb,var(--svf-ph) 30%,transparent); }
.sv-familybar a[data-sv="lastmile"] { color:var(--svf-lm); border-color:color-mix(in srgb,var(--svf-lm) 30%,transparent); }
.sv-familybar a[data-sv]:hover { border-color:currentColor; }
body[data-sv-site="secondchance"] .sv-familybar a[data-sv="secondchance"],
body[data-sv-site="powerhouse"] .sv-familybar a[data-sv="powerhouse"],
body[data-sv-site="lastmile"] .sv-familybar a[data-sv="lastmile"] { background:color-mix(in srgb,currentColor 14%,transparent); font-weight:600; cursor:default; }
</style>